import { Link } from "@inertiajs/react"
import { Users } from "lucide-react"
import { useTranslation } from "react-i18next"

import { MasterclassCompletionsRosterRow } from "@/components/masterclasses/masterclass-completions-roster-row"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { crewPath } from "@/routes"
import type {
  MasterclassCompletionsPayload,
  MasterclassState,
} from "@/types/masterclasses"

/**
 * OKS-57 — Per-masterclass Completions card (`Brand/Masterclasses/Show`,
 * slotted between Overview and Modules placeholder).
 *
 * Reads the OKS-57 `completions` Inertia prop + a 2-key discriminator
 * derived from the parent `masterclass` (`state` + `published_at`). All
 * four UX Spec §3 states are branched here (TRD KD-4 — single-shape
 * payload, FE picks the rendering):
 *
 *  1. Pure draft (state="draft" + published_at=null) → muted placeholder
 *     "Not published yet — your crew can't see this training."
 *  2. Empty (≥1 published OR currently-unpublished, but `total_active=0`)
 *     → "No active crew yet" + CTA to `crewPath()`.
 *  3. Published with crew OR currently-unpublished-with-history →
 *     headline ratio + percent + "Historical" badge (if unpublished) +
 *     roster.
 *  4. Published with crew but 0 completions → headline "0/Y (0%)" +
 *     roster of all "Not started" rows (no special empty layer — already
 *     handled by the full roster paint).
 *
 * Accessibility (UX Spec §6):
 *  - Headline stat block is `role="group"` with an `aria-label` summarising
 *    the ratio + percent ("12 of 50 creators completed, 24 percent").
 *  - Status dots in row are `aria-hidden`; text-status pairs colour with
 *    a real label that SR reads.
 *  - Empty-state icon is `aria-hidden`; copy is the SR-visible content.
 */
export interface MasterclassCompletionsSectionProps {
  completions: MasterclassCompletionsPayload
  /** Parent masterclass state — drives the pure-draft branch. */
  masterclassState: MasterclassState
  /** Parent masterclass `published_at` ISO or null. */
  masterclassPublishedAt: string | null
}

export function MasterclassCompletionsSection({
  completions,
  masterclassState,
  masterclassPublishedAt,
}: MasterclassCompletionsSectionProps) {
  const { t } = useTranslation()

  const isPureDraft =
    masterclassState === "draft" && masterclassPublishedAt === null

  const {
    total_active,
    completed_count,
    percent,
    currently_unpublished,
    members,
  } = completions

  // Pure draft (state 1) — single muted line, no stats, no list. The
  // controller already zeroes the payload for this branch (KD-4
  // short-circuit) but we re-check FE-side because the discriminator
  // (`state` + `published_at`) is the authoritative source — keeps the
  // section coherent if the payload ever drifts mid-flight (defensive).
  if (isPureDraft) {
    return (
      <Card data-testid="masterclass-completions-card" data-state="draft">
        <CardHeader>
          <CardTitle>{t("masterclasses.show.completions.title")}</CardTitle>
        </CardHeader>
        <CardContent
          data-testid="masterclass-completions-draft-placeholder"
          className="text-muted-foreground text-sm"
        >
          {t("masterclasses.show.completions.draft_placeholder")}
        </CardContent>
      </Card>
    )
  }

  // Empty (state 2) — published / unpublished-with-history but brand has
  // zero active crew. Render the "No active crew yet" panel + CTA.
  if (total_active === 0) {
    return (
      <Card
        data-testid="masterclass-completions-card"
        data-state="empty-no-crew"
      >
        <CardHeader>
          <CardTitle>{t("masterclasses.show.completions.title")}</CardTitle>
        </CardHeader>
        <CardContent
          data-testid="masterclass-completions-empty-no-crew"
          className="flex flex-col items-center gap-3 border-t border-dashed py-8 text-center"
        >
          <Users className="text-muted-foreground size-8" aria-hidden="true" />
          <p className="text-base font-medium">
            {t("masterclasses.show.completions.empty_no_crew_title")}
          </p>
          <p className="text-muted-foreground max-w-md text-sm">
            {t("masterclasses.show.completions.empty_no_crew_body")}
          </p>
          <Button
            asChild
            variant="outline"
            data-testid="masterclass-completions-empty-cta"
          >
            <Link href={crewPath()}>
              {t("masterclasses.show.completions.empty_no_crew_cta")}
            </Link>
          </Button>
        </CardContent>
      </Card>
    )
  }

  // States 3 + 4 — render full headline + roster.
  return (
    <Card
      data-testid="masterclass-completions-card"
      data-state={
        currently_unpublished ? "unpublished-historical" : "published"
      }
    >
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle>{t("masterclasses.show.completions.title")}</CardTitle>
          {currently_unpublished && (
            <Badge
              variant="secondary"
              data-testid="masterclass-completions-historical-badge"
            >
              {t("masterclasses.show.completions.historical_badge")}
            </Badge>
          )}
        </div>
        <div
          role="group"
          aria-label={t("masterclasses.show.completions.sr_summary", {
            completed: completed_count,
            total: total_active,
            percent,
          })}
          className="flex items-baseline gap-1.5"
          data-testid="masterclass-completions-headline"
        >
          <span className="text-2xl font-semibold tracking-tight tabular-nums">
            {t("masterclasses.show.completions.headline_ratio", {
              completed: completed_count,
              total: total_active,
            })}
          </span>
          <span className="text-muted-foreground text-sm">
            {t("masterclasses.show.completions.headline_percent", { percent })}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <ul
          data-testid="masterclass-completions-roster"
          className="flex flex-col"
        >
          {members.map((m) => (
            <MasterclassCompletionsRosterRow key={m.membership_id} member={m} />
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}
